import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, brl, fmtDateTime } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Receipt, Plane } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";

export default function Billing() {
  const [pending, setPending] = useState([]);
  const [invoiced, setInvoiced] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchAll = async () => {
    setLoading(true);
    const [a, b] = await Promise.all([
      api.get("/work-orders", { params: { status: "AGUARDANDO_FATURAMENTO" } }),
      api.get("/work-orders", { params: { status: "FATURADO" } }),
    ]);
    setPending(a.data);
    setInvoiced(b.data);
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto">
      <div className="mb-6">
        <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500">Back-office</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-slate-950">Faturamento</h1>
        <p className="text-sm text-slate-600 mt-1">Aplique preços, revise dados e emita a nota.</p>
      </div>

      <h2 className="font-display font-bold text-lg tracking-tight mb-2">Aguardando Faturamento</h2>
      <Card className="rounded-sm border-slate-200 overflow-hidden mb-8">
        <Table
          rows={pending}
          loading={loading}
          onOpen={(wo) => navigate(`/os/${wo.id}`)}
          actionLabel="Precificar"
          testidPrefix="pending"
        />
      </Card>

      <h2 className="font-display font-bold text-lg tracking-tight mb-2">Faturadas</h2>
      <Card className="rounded-sm border-slate-200 overflow-hidden">
        <Table
          rows={invoiced}
          loading={loading}
          onOpen={(wo) => navigate(`/faturamento/${wo.id}/preview`)}
          actionLabel="Ver Nota A4"
          testidPrefix="invoiced"
        />
      </Card>
    </div>
  );
}

function Table({ rows, loading, onOpen, actionLabel, testidPrefix }) {
  return (
    <div className="overflow-x-auto ag-scroll">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
            <th className="py-3 px-4 font-semibold">Código</th>
            <th className="py-3 px-4 font-semibold">Prefixo</th>
            <th className="py-3 px-4 font-semibold">Cliente</th>
            <th className="py-3 px-4 font-semibold">Chegada</th>
            <th className="py-3 px-4 font-semibold">Status</th>
            <th className="py-3 px-4 font-semibold text-right">Total</th>
            <th className="py-3 px-4 font-semibold text-right">Ações</th>
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan={7} className="py-8 text-center text-slate-400">Carregando…</td></tr>}
          {!loading && rows.length === 0 && (
            <tr><td colSpan={7} className="py-10 text-center text-slate-400 text-sm">Nada por aqui.</td></tr>
          )}
          {!loading && rows.map((wo) => (
            <tr key={wo.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`${testidPrefix}-row-${wo.code}`}>
              <td className="py-3 px-4 font-mono text-xs font-semibold text-slate-900">{wo.code}</td>
              <td className="py-3 px-4 font-mono uppercase">{wo.tail_number}</td>
              <td className="py-3 px-4 text-slate-700">{wo.client_name || "—"}</td>
              <td className="py-3 px-4 font-mono text-xs text-slate-600">{fmtDateTime(wo.arrival_datetime)}</td>
              <td className="py-3 px-4"><StatusBadge status={wo.status} /></td>
              <td className="py-3 px-4 text-right font-mono tabular-nums">
                {wo.invoice_total != null ? brl(wo.invoice_total) : <span className="text-slate-400">—</span>}
              </td>
              <td className="py-3 px-4 text-right">
                <Button size="sm" variant="outline" className="rounded-sm" onClick={() => onOpen(wo)} data-testid={`${testidPrefix}-open-${wo.code}`}>
                  <Receipt className="w-3.5 h-3.5 mr-1" /> {actionLabel}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
